<template>
  <div class="user-settings" v-if="user">
    <div class="toggle">
      <input
        type="checkbox"
        v-model="invisible"
        @change="handleChange"
        :id="inSession ? 'visibility-in-session' : 'visibility'"
      />
      <label class="toggle__switch" :for="inSession ? 'visibility-in-session' : 'visibility'">
        <span class="switch" :class="invisible ? 'switch-off' : undefined"></span>
        <span>Hide me in chat</span>
      </label>
    </div>
    <div class="icon-container">
      <tooltip-info
        ref="tooltipRef"
        :is-visible="tooltipVisible"
        custom
        persistent
        title="Chat Visibility"
        :text="invisible ? 'Turn off hiding to start chatting' : 'Turn on hiding to not be shown to other members in OmniChat'"
        x-position="center"
        @close="handleCloseAtteendeesTooltip"
      >
        <template #actions>
          <pge-button
            :label="invisible ? 'Stay Hidden' : 'Stay Visible'"
            template="text"
            button-size="small"
            @click="handleCloseAtteendeesTooltip"
          />
        </template>
      </tooltip-info>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch, computed } from 'vue';
import PgeButton from "./shared/PgeButton.vue";
import TooltipInfo from "./shared/tooltip/TooltipInfo.vue";
import { useSocketStore } from '../../stores/socketStore';
import { useAuthStore } from '../../stores/auth';

const emit = defineEmits(['change', 'hiddenTooltip', 'showTooltip']);
const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  newUser: {
    type: Object,
    required: true
  },
  tooltip: {
    type: Boolean,
    default: false,
  },
  inSession: {
    type: Boolean,
    default: false,
  },
});

const socketStore = useSocketStore();
const authStore = useAuthStore();

const invisible = ref(props.newUser ? props.newUser.invisible : false);

// Check if tour is completed. usually it is string "1" or "0".
const isTourCompleted = computed(() => authStore.userData?.tour_completed === "1");

const shouldShowTooltip = () => {
  if (!isTourCompleted.value) return false;
  return localStorage.getItem("alreadyShowedTooltip") ? false : props.tooltip;
};

const tooltipVisible = ref(shouldShowTooltip());

if (tooltipVisible.value) {
  localStorage.setItem("alreadyShowedTooltip", "true");
}

const tooltipRef = ref(null);

watch(isTourCompleted, (newVal) => {
  if (newVal && !localStorage.getItem("alreadyShowedTooltip") && props.tooltip) {
    tooltipVisible.value = true;
    localStorage.setItem("alreadyShowedTooltip", "true");
  }
});

watch(() => props.newUser, (newVal) => {
  invisible.value = newVal.invisible;
}, { deep: true });

watch(() => props.tooltip, (newVal) => {
  // Only show if tour is completed
  if (isTourCompleted.value) {
    tooltipVisible.value = newVal;
    if (newVal) {
      localStorage.setItem("alreadyShowedTooltip", "true");
    }
  }
});

const handleChange = () => {
  socketStore.emit("user_visibility", {
    domain: window.store.subdomain,
    user_id: props.user.id,
    invisible: invisible.value,
  });
  emit('change', invisible.value);
  hideTooltip();
};

const handleCloseAtteendeesTooltip = () => {
  tooltipRef.value.closeTooltip();
  emit('hiddenTooltip');
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  emit('hiddenTooltip');
};

const showTooltip = () => {
  tooltipVisible.value = true;
  emit('showTooltip');
};
</script>

<style lang="scss" scoped>
.user-settings {
  position: relative;
  display: flex;
  column-gap: 2px;
  flex-shrink: 1;
  align-items: center;
  padding: 0 10px;
  color: #000;

  $width: 34px;
  $height: 18px;

  .toggle {
    &__switch {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    input[type="checkbox"] {
      position: absolute;
      height: 0;
      width: 0;
      visibility: hidden;
    }

    span {
      cursor: pointer;
      color: #4B444D;
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
    }

    .switch {
      display: block;
      position: relative;

      &:before {
        content: "";
        cursor: pointer;
        text-indent: -9999px;
        width: 30px;
        height: 17.5px;
        background: #E9E0E7;
        border: 1.25px solid #7D747E;
        display: block;
        border-radius: 62.5px;
        padding: 1.25px, 2.5px, 1.25px, 2.5px;
        position: relative;
      }

      &:after {
        content: url("data:image/svg+xml;base64,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");
        position: absolute;
        top: 2px;
        left: 3px;
        display: flex;
        justify-content: center;
        width: 15px;
        height: 15px;
        background: #7D747E;
        border-radius: 15px;
        transition: 0.3s;
      }
    }

    .switch-off {
      &:after {
        transform: translateX(12.5px);
        content: url("data:image/svg+xml;base64,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");
      }
    }

    input:checked + label span:before {
      background: #5E2F78;
      border: 1.25px solid #5E2F78;
    }

    input:checked + label span:after {
      transform: translateX(12.5px);
      background: #fff;
    }
  }

  .icon-container {
    height: 40px;
    align-content: center;

    svg {
      margin-left: 10px;
      cursor: pointer;
    }

    .tooltip {
      position: absolute;
      width: auto;
      min-width: 220px;
      padding: 8px;
      left: 0;
      top: 40px;
      border-radius: 12px;
      background: #F5EBF3;
      box-shadow: 0px 2px 6px 2px #00000026, 0px 1px 2px 0px #0000004D;
      opacity: 1;
      pointer-events: auto;
      z-index: 2;
      transform-origin: center;
      animation-duration: 300ms;
      animation-timing-function: cubic-bezier(.2, 0, 0, 1);

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        letter-spacing: 0.15px;
        text-align: left;
        color: #49454F;
      }

      p {
        font-family: Lato, sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.2px;
        text-align: left;
        color: #4B444D;
      }

      button {
        font-family: Lato, sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0.1px;
        text-align: center;
        color: #5E2F78;
      }
    }
  }
}

.tooltip-enter {
  animation-name: scaleUp;
  opacity: 1 !important;
  z-index: 2 !important;
}

.tooltip-exit {
  animation-name: scaleDown;
  opacity: 0 !important;
  z-index: -1 !important;
}

@keyframes scaleUp {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    z-index: 2;
    opacity: 1;
  }
}

@keyframes scaleDown {
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0);
    z-index: -1;
    opacity: 0;
  }
}

/* ===================================================================
   Northstar (in-session panel): "Hide me in chat" as a quiet row label
   with the room's small switch (.sw.sm: 30×18, 12px knob). Applies only
   under [data-xl-gen="northstar"]; elsewhere the Generation 1 eye switch
   stays.
   =================================================================== */
[data-xl-gen="northstar"] .user-settings {
  flex: 1 1 auto;
  min-width: 0;
  column-gap: 0;
  padding: 0 4px 0 6px;
  font-family: var(--xl-font-family, 'General Sans', Inter, -apple-system, 'Segoe UI', sans-serif);

  .toggle {
    min-width: 0;

    &__switch {
      justify-content: flex-start;
      gap: 8px;
      min-width: 0;
    }

    span {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
      letter-spacing: -0.006em;
      color: var(--xl-color-on-surface-variant, #514A57);
      white-space: nowrap;
    }

    .switch {
      flex: 0 0 30px;
      width: 30px;
      height: 18px;

      &:before {
        width: 30px;
        height: 18px;
        padding: 0;
        border: 0;
        border-radius: 99px;
        background: #DAD3DE;
        transition: background 0.16s;
      }

      &:after {
        content: "";
        top: 3px;
        left: 3px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 2px rgba(28, 23, 32, 0.18);
        transform: none;
        transition: left 0.16s;
      }
    }

    .switch-off:after {
      content: "";
      transform: none;
    }

    input:checked + label span.switch:before {
      border: 0;
      background: linear-gradient(155deg, var(--xl-color-surface-tint, #8154A6), var(--xl-color-primary, #5E2F78));
    }

    input:checked + label span.switch:after {
      left: 15px;
      background: #fff;
      transform: none;
    }
  }

  .icon-container {
    height: 32px;
    flex: 0 0 auto;

    :deep(.tooltip__icon) {
      display: grid;
      place-items: center;
      width: 24px;
      height: 24px;
      margin-left: 2px;
      border-radius: 7px;
      cursor: pointer;
    }

    :deep(.tooltip__icon:hover) {
      background: var(--xl-color-border-subtle, #F6F2F8);
    }

    :deep(svg) {
      margin-left: 0;
    }

    :deep(svg path) {
      fill: var(--xl-color-outline, #867D8D);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  [data-xl-gen="northstar"] .user-settings .toggle .switch:before,
  [data-xl-gen="northstar"] .user-settings .toggle .switch:after {
    transition: none;
  }
}
</style>
